import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Board, Priority, Sharing } from '@cortex/domain-org-issues';
import { PRIORITIES, SHARINGS } from '@cortex/domain-org-issues/model';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { MultiPicker } from '@cortex/web-design-system/components/ui/MultiPicker';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useCreateOrgIssue, useOrgBoardMembers } from '../../../hooks/useOrgIssues.js';
import type { TeamMeetingPostSource } from '../../../lib/api/org-issues.js';
import { parseLabels, personOptions } from '../../../lib/org-issues.js';

/** @graph-connects none */
interface CreateIssueModalProps {
  board: Board;
  open: boolean;
  onClose: () => void;
  onCreated: (issueId: string) => void;
  /** 自動入力の初期値（週次MTG の投稿から起票するとき）。どれも人が直してから起票する */
  initial?: { title?: string; body?: string };
  /** 起票する課題の起点。省くと画面から作った課題（manual） */
  source?: TeamMeetingPostSource;
}

/**
 * 課題を作る。本文はボードのテンプレートを初期値にし、列を選ばなければ API が
 * 「一番左の未着手の列」に入れる（どの列に入れるかの規則を画面に写さない）。
 *
 * 週次MTG の投稿から起票するときは `initial`（タイトル・本文の初期値）と `source`（起点）を渡す。
 * 起点は欄に出さず、作る課題にだけ付ける。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードにタイトル・本文（テンプレートつき）・列・優先度・担当・ラベル・共有状態を指定して課題を起票する
 * @graph-connects useCreateOrgIssue [calls] 課題の作成
 * @graph-connects useOrgBoardMembers [calls] 担当の候補
 */
export function CreateIssueModal({
  board,
  open,
  onClose,
  onCreated,
  initial,
  source,
}: CreateIssueModalProps): ReactNode {
  const { t } = useTranslation();
  const create = useCreateOrgIssue(board.id);
  const members = useOrgBoardMembers(board.id);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? board.bodyTemplate);
  const [columnId, setColumnId] = useState('');
  const [priority, setPriority] = useState<Priority | null>(null);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [labels, setLabels] = useState('');
  const [sharing, setSharing] = useState<Sharing>('shared');
  const columns = [...board.columns].sort((a, b) => a.position - b.position);

  return (
    <Modal open={open} onClose={onClose} size="xl" title={t('orgIssues.issue.createHeading')}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            {
              title,
              body,
              priority,
              assignees,
              labels: parseLabels(labels),
              sharing,
              ...(columnId === '' ? {} : { columnId }),
              ...(source === undefined ? {} : { source }),
            },
            {
              onSuccess: (view) => {
                onCreated(view.issue.id);
              },
            },
          );
        }}
      >
        <Input
          label={t('orgIssues.issue.title')}
          value={title}
          fullWidth
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
        <label className="block space-y-1 text-sm text-[var(--color-text-secondary)]">
          <span>{t('orgIssues.issue.body')}</span>
          <Textarea
            rows={8}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
            }}
          />
        </label>
        <div className="grid gap-3 md:grid-cols-2">
          <Select
            label={t('orgIssues.issue.column')}
            value={columnId}
            options={[
              { value: '', label: t('orgIssues.issue.defaultColumn') },
              ...columns.map((column) => ({ value: column.id, label: column.label })),
            ]}
            onChange={(e) => {
              setColumnId(e.target.value);
            }}
          />
          <Select
            label={t('orgIssues.issue.priority')}
            value={priority ?? ''}
            options={[
              { value: '', label: t('orgIssues.priority.none') },
              ...PRIORITIES.map((value) => ({ value, label: t(`orgIssues.priority.${value}`) })),
            ]}
            onChange={(e) => {
              setPriority(PRIORITIES.find((value) => value === e.target.value) ?? null);
            }}
          />
          <MultiPicker
            label={t('orgIssues.issue.assignees')}
            placeholder={t('orgIssues.issue.assigneesPlaceholder')}
            options={personOptions(members.data?.people ?? {}, members.data?.members ?? [])}
            value={assignees}
            searchable
            onChange={setAssignees}
          />
          <Input
            label={t('orgIssues.issue.labels')}
            value={labels}
            onChange={(e) => {
              setLabels(e.target.value);
            }}
          />
          <Select
            label={t('orgIssues.issue.sharing')}
            helperText={t('orgIssues.issue.sharingHelp')}
            value={sharing}
            options={SHARINGS.map((value) => ({ value, label: t(`orgIssues.sharing.${value}`) }))}
            onChange={(e) => {
              setSharing(SHARINGS.find((value) => value === e.target.value) ?? 'shared');
            }}
          />
        </div>
        {create.isError && (
          <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
            {create.error.message}
          </p>
        )}
        <div className="flex gap-2">
          <Button type="submit" size="sm" loading={create.isPending}>
            {t('orgIssues.issue.create')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t('orgIssues.common.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
